import { type ReactNode, useState } from 'react';

import { PasswordPage } from '~/components/PasswordPage/PasswordPage';

export type ShareLinkPasscodeGateProps<TVerifiedData> = {
  shortId: string;
  /**
   * Fetches the route's data after the visitor verifies — the client-side twin of the route type's
   * getStaticProps case. A protected link's data can only ever be fetched client-side: ISR renders
   * with no request context and a shared cache, so the server can never see a visitor's passcode
   * token — the verified visitor's browser (the only place the token exists, in-memory on the
   * @air/api client) has to fetch the protected data itself.
   */
  fetchVerifiedData: () => Promise<TVerifiedData>;
  /** Renders the route's tree with the fetched data once the visitor verifies. */
  children: (verifiedData: TVerifiedData) => ReactNode;
};

/**
 * The share route's passcode gate, mounted ONLY for passcode-protected links (an unprotected link
 * renders its tree straight from ISR props, no gate involved): shows the passcode screen until the
 * visitor verifies, then renders the route's tree with the data `fetchVerifiedData` fetched.
 * Fetched-data presence IS the verification state — there is no separate boolean, and children
 * never see undefined.
 *
 * Each route type mounts its own gate with a type-specific fetcher, so the data is monomorphic —
 * no page-level discriminated state, and the route dispatch happens exactly once (in the page's
 * branch on the shortId prefix), not again inside a shared onVerified switch.
 *
 * Generic render-prop component, so no `memo` — wrapping would erase the type parameter, and it
 * renders once per page load.
 */
export const ShareLinkPasscodeGate = <TVerifiedData,>({
  shortId,
  fetchVerifiedData,
  children,
}: ShareLinkPasscodeGateProps<TVerifiedData>) => {
  const [verifiedData, setVerifiedData] = useState<TVerifiedData | undefined>(undefined);

  if (verifiedData === undefined) {
    return (
      <PasswordPage
        shortId={shortId}
        onVerified={async () => {
          setVerifiedData(await fetchVerifiedData());
        }}
      />
    );
  }

  return <>{children(verifiedData)}</>;
};
